/**
 * A busy map made readable without hiding what moves (its documents keep moving on every flow that is left):
 *
 * 1. A hub's steps as one node. Data models of one system that pass data on to each other (Datadog's timeseries →
 *    service checks → queue) become one node when HUB_MIN or more flows reach them from outside, or HUB_MIN or more
 *    integrations pass data between them: one box with a row per step, and the flows between the steps are gone.
 * 2. Families. The same data model in HUB_MIN or more systems (CimcorpAtom, CitycomAtom, NetrautaAtom: one process
 *    copied per customer) becomes one node, "Atom ×7", in a lane of its own; the copies' parallel flows become one
 *    flow that counts them. A family the viewer opened (`open`) is shown as it is.
 *
 * Pure: the page passes what Now's adapter made and the families opened, and gets nodes, flows and the lanes'
 * labels of the families.
 */
import type { MapFlow, MapNode } from './types.ts';
import { count } from '../lib/format.ts';

/** From how many flows in a hub's steps become one node, and from how many systems copies become a family. */
export const HUB_MIN = 3;

export interface FamilyLane { label: string; systems: string[] }
export interface Simplified {
  nodes: MapNode[];
  flows: MapFlow[];
  /** The lanes of families: their label and the systems they stand for (by the lane's code). */
  lanes: Map<string, FamilyLane>;
  /** The families made (key → member ids), for the page to offer opening them. */
  families: Map<string, { label: string; members: string[] }>;
}

export const FAMILY_PREFIX = 'family:';

/** The object of a data model without its system's name in front: CimcorpAtom (CIMCORP) → Atom; TimestampA → Timestamp. */
export function objectOf(cls: string, system: string): string | null {
  const code = system.replace(/[^A-Za-z0-9]/g, '');
  if (!code || cls.length <= code.length || cls.slice(0, code.length).toLowerCase() !== code.toLowerCase()) return null;
  const rest = cls.slice(code.length);
  if (!/^[A-Z0-9_]/.test(rest)) return null;
  return rest.replace(/(?<=[a-z])[A-Z]$/, '');
}

export function simplifyMap(nodesIn: MapNode[], flowsIn: MapFlow[], open: ReadonlySet<string> = new Set(), labelOf: (system: string) => string = (s) => s): Simplified {
  let nodes = nodesIn.map((n) => ({ ...n }));
  let flows = flowsIn.map((f) => ({ ...f }));
  const byId = () => new Map(nodes.map((n) => [n.id, n]));

  // ---- 1. hubs: a system's steps reached by many flows from outside become one node
  {
    const at = byId();
    const parent = new Map<string, string>();
    const find = (x: string): string => { const p = parent.get(x) ?? x; if (p === x) return x; const r = find(p); parent.set(x, r); return r; };
    for (const f of flows) {
      if (!f.to || f.to === f.from) continue;
      const a = at.get(f.from), b = at.get(f.to);
      if (a && b && a.system === b.system) parent.set(find(f.from), find(f.to));
    }
    const groups = new Map<string, string[]>();
    for (const n of nodes) groups.set(find(n.id), [...(groups.get(find(n.id)) ?? []), n.id]);
    for (const members of groups.values()) {
      if (members.length < 2) continue;
      const set = new Set(members);
      // Many flows reach the steps from outside (Now), or many integrations pass data between them (a search for them)
      const fromOutside = flows.filter((f) => f.to && set.has(f.to) && !set.has(f.from)).length;
      const inside = new Set(flows.filter((f) => f.to && set.has(f.to) && set.has(f.from) && f.to !== f.from).map((f) => f.process)).size;
      if (fromOutside < HUB_MIN && inside < HUB_MIN) continue;
      const parts = members.map((id) => at.get(id)!).sort((a, b) => (b.count ?? 0) - (a.count ?? 0) || a.id.localeCompare(b.id));
      const head = parts[0];
      const total = parts.reduce((acc, p) => acc + (p.count ?? 0), 0);
      const failed = parts.reduce((acc, p) => acc + (p.failed ?? 0), 0);
      const last = Math.max(...parts.map((p) => p.last ?? 0)) || null;
      const merged: MapNode = {
        ...head, label: labelOf(head.system), count: total, failed: failed || undefined, last,
        detail: `${count(total)} today`,
        rows: parts.map((p) => `› ${p.label} · ${count(p.count ?? 0)}`),
        members: parts.flatMap((p) => p.members ?? [p.id]),
      };
      nodes = [...nodes.filter((n) => !set.has(n.id)), merged];
      flows = flows
        .map((f) => ({ ...f, from: set.has(f.from) ? head.id : f.from, to: f.to && set.has(f.to) ? head.id : f.to }))
        .filter((f) => !(f.to === head.id && f.from === head.id));
    }
  }

  // ---- 2. families: the same object in many systems becomes one node in a lane of its own
  const lanes = new Map<string, FamilyLane>();
  const families = new Map<string, { label: string; members: string[] }>();
  {
    const byObject = new Map<string, MapNode[]>();
    for (const n of nodes) {
      const o = objectOf(n.id, n.system);
      if (o) byObject.set(o.toLowerCase(), [...(byObject.get(o.toLowerCase()) ?? []), n]);
    }
    // One lane for the families of the same systems (the customers whose copies they are)
    const laneOf = new Map<string, string>();
    for (const [key, members] of byObject) {
      const systems = [...new Set(members.map((m) => m.system))].sort();
      if (systems.length < HUB_MIN || open.has(FAMILY_PREFIX + key)) continue;
      const laneKey = systems.join(',');
      if (!laneOf.has(laneKey)) {
        const code = `×${systems.length}·${laneOf.size + 1}`;
        laneOf.set(laneKey, code);
        lanes.set(code, { label: `${systems.length} systems`, systems });
      }
      const code = laneOf.get(laneKey)!;
      const id = FAMILY_PREFIX + key;
      const object = objectOf(members[0].id, members[0].system)!;
      const total = members.reduce((acc, m) => acc + (m.count ?? 0), 0);
      const failed = members.reduce((acc, m) => acc + (m.failed ?? 0), 0);
      const fam: MapNode = {
        id, label: `${object} ×${systems.length}`, system: code, format: members[0].format, count: total, countLabel: members[0].countLabel,
        last: Math.max(...members.map((m) => m.last ?? 0)) || null, usual: null, failed: failed || undefined, failedWindow: members[0].failedWindow,
        detail: `${count(total)} today`, rows: [systems.slice(0, 4).map(labelOf).join(' · ') + (systems.length > 4 ? ` +${systems.length - 4}` : '')],
        members: members.flatMap((m) => m.members ?? [m.id]),
      };
      families.set(id, { label: fam.label, members: members.map((m) => m.id) });
      const set = new Set(members.map((m) => m.id));
      nodes = [...nodes.filter((n) => !set.has(n.id)), fam];
      flows = flows.map((f) => ({ ...f, from: set.has(f.from) ? id : f.from, to: f.to && set.has(f.to) ? id : f.to })).filter((f) => f.from !== f.to);
    }
  }

  // ---- the copies' parallel flows (now between the same two nodes) become one that counts them
  {
    const byPair = new Map<string, MapFlow[]>();
    for (const f of flows) byPair.set(`${f.from}→${f.to ?? `out:${f.external ?? ''}`}`, [...(byPair.get(`${f.from}→${f.to ?? `out:${f.external ?? ''}`}`) ?? []), f]);
    flows = [...byPair.values()].map((fs) => {
      if (fs.length === 1) return fs[0];
      const processes = [...new Set(fs.map((f) => f.process))];
      const runs = fs.reduce((acc, f) => acc + f.runs, 0);
      const failed = fs.reduce((acc, f) => acc + f.failed, 0);
      const lead = [...fs].sort((a, b) => b.failed - a.failed || b.runs - a.runs)[0];
      return {
        ...lead, id: `${lead.from}→${lead.to ?? 'out'}·${processes.length}`, runs, failed, split: undefined, merge: undefined,
        label: processes.length > 1 ? `${processes.length} integrations · ${count(runs)} runs${failed ? ` · ${count(failed)} failed` : ''}` : lead.label,
      };
    });
  }
  return { nodes, flows, lanes, families };
}
